import { cloneDeep, get, isEmpty } from 'lodash';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  ExpertReportType,
  IExpertGlobalReportState,
} from 'containers/common/LegalProceedings/ExpertReport/typings';
import { JudgementADDDocumentType } from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import {
  isAccountingExpertisesValidated,
  isMechanicalExpertisesValidated,
  isMedicalExpertisesValidated,
} from 'containers/common/LegalProceedings/ExpertReport/FormsByExpertiseType/form-validations';
import {
  distinctExpertReport,
  formatExpertReportState,
  formatSavedExpertReportToMatchState,
} from 'containers/common/LegalProceedings/ExpertReport/FormsByExpertiseType/utils';
import { EXPERTISES } from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/constants';

export const useExpertReportValidation = (expertise: any, type: string) => {
  const [isExpertiseValidated, setIsExpertiseValidated] = useState<boolean>(
    false,
  );
  const expertiseId = get(expertise, 'expertiseId', '');

  useEffect(() => {
    const isExpertiseValidated =
      String(expertiseId) === EXPERTISES.MEDICAL
        ? isMedicalExpertisesValidated(expertise)
        : String(expertiseId) === EXPERTISES.ACCOUNTING
        ? isAccountingExpertisesValidated(expertise, type)
        : String(expertiseId) === EXPERTISES.MECHANICAL &&
          isMechanicalExpertisesValidated(expertise);
    setIsExpertiseValidated(isExpertiseValidated);
  }, [expertise, type]);

  return isExpertiseValidated;
};

export const useFormatExpertReportState = (
  judgementAddDocuments: JudgementADDDocumentType[],
  savedExpertReport: ExpertReportType = {} as any,
): [
  IExpertGlobalReportState[],
  Dispatch<SetStateAction<IExpertGlobalReportState[]>>,
] => {
  const { newJADDExpertises, oldJADDExpertises } = distinctExpertReport(
    judgementAddDocuments,
    savedExpertReport,
  );

  const [expertReport, setExpertReport] = useState<IExpertGlobalReportState[]>(
    [],
  );
  let formattedExpertReportState: IExpertGlobalReportState[] = [];
  if (!isEmpty(oldJADDExpertises)) {
    const report = formatSavedExpertReportToMatchState(
      oldJADDExpertises,
      savedExpertReport,
    );
    formattedExpertReportState = [...formattedExpertReportState, ...report];
  }
  if (!isEmpty(newJADDExpertises)) {
    const report = formatExpertReportState(newJADDExpertises);
    const existingExpertises = formattedExpertReportState.filter(item =>
      report.some(el => el.id === item.id),
    );
    const newExpertises = report.filter(item =>
      formattedExpertReportState.every(el => {
        return el.id !== item.id;
      }),
    );
    existingExpertises.forEach((item: any) =>
      report.forEach((jadd: any) => {
        get(item, 'expertises', []).forEach((expertise: any) => {
          get(jadd, 'expertises', []).forEach((newExpertise: any) => {
            if (
              expertise.expertiseJudgementAddDocumentId ===
              newExpertise.expertiseJudgementAddDocumentId
            ) {
              expertise.victims = [
                ...get(expertise, 'victims', []),
                ...get(newExpertise, 'victims', []),
              ];
              expertise.vehicules = [
                ...get(expertise, 'vehicules', []),
                ...get(newExpertise, 'vehicules', []),
              ];
            } else if (
              expertise.expertiseJudgementAddDocumentId !==
                newExpertise.expertiseJudgementAddDocumentId &&
              item.id === jadd.id
            ) {
              const expertiseExists =
                get(item, 'expertises', []).find(
                  (el: any) =>
                    el.expertiseJudgementAddDocumentId ===
                    newExpertise.expertiseJudgementAddDocumentId,
                ) || {};
              isEmpty(expertiseExists) &&
                get(item, 'expertises', []).push(newExpertise);
            }
          });
        });
      }),
    );

    if (!isEmpty(newExpertises)) {
      formattedExpertReportState = [
        ...formattedExpertReportState,
        ...newExpertises,
      ];
    }

    if (isEmpty(savedExpertReport)) formattedExpertReportState = [...report];
  }

  useEffect(() => {
    setExpertReport(cloneDeep(formattedExpertReportState));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [judgementAddDocuments, savedExpertReport]);

  return [expertReport, setExpertReport];
};
